import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, MagnifyingGlassPlus, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASELINE_VISIT_CODE = 'T0';

/**
 * Side-by-side comparison popup: Baseline (T0) + Current visit photos + optional
 * reference scale image. Triggered from DermScaleRenderer / MasiRenderer.
 * URS-F217.
 *
 * Props:
 *   studyId, volunteerCode, currentVisitCode — to fetch photos
 *   scaleId? — optional, when shown next to a derm_scale (renders 3rd column with chart)
 *   referenceImageUrl? — alternative direct URL (when not a scale)
 *   triggerLabel?
 *   testIdPrefix?
 */
export function VolunteerPhotoCompare({ studyId, volunteerCode, currentVisitCode, scaleId, referenceImageUrl, triggerLabel, testIdPrefix = 'photo-cmp' }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scaleMeta, setScaleMeta] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);  // URL of photo being zoomed
  const [zoomMeta, setZoomMeta] = useState(null);

  useEffect(() => {
    if (!open || !studyId || !volunteerCode) return;
    setLoading(true);
    const promises = [
      axios.get(`${API_URL}/api/studies/${studyId}/volunteer-photos`, {
        withCredentials: true,
        params: { volunteer_code: volunteerCode },
      }).then(r => setPhotos(r.data || [])).catch(() => setPhotos([])),
    ];
    if (scaleId) {
      promises.push(
        axios.get(`${API_URL}/api/config/derm-scales/${scaleId}`, { withCredentials: true })
          .then(r => setScaleMeta(r.data))
          .catch(() => setScaleMeta(null))
      );
    }
    Promise.all(promises).finally(() => setLoading(false));
  }, [open, studyId, volunteerCode, scaleId]);

  // Group photos by visit_code (each visit can have N photos across devices)
  const byVisit = photos.reduce((acc, p) => {
    if (!acc[p.visit_code]) acc[p.visit_code] = [];
    acc[p.visit_code].push(p);
    return acc;
  }, {});

  const baselinePhotos = byVisit[BASELINE_VISIT_CODE] || [];
  const currentPhotos = byVisit[currentVisitCode] || [];

  const Column = ({ title, subtitle, items, accent = 'stone' }) => (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className={`text-xs font-semibold text-${accent}-700 uppercase tracking-wider mb-1.5 px-1`}>
        {title}
        {subtitle && <span className="ml-2 text-stone-400 normal-case font-normal">({subtitle})</span>}
      </div>
      <div className="flex-1 bg-stone-50 border border-stone-200 rounded-sm p-2 min-h-[200px] overflow-y-auto">
        {items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-stone-400 text-xs gap-2">
            <ImageIcon size={32} className="opacity-30" />
            <span className="italic">Sin fotos disponibles</span>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(p => {
              const src = `${API_URL}/api/studies/${studyId}/volunteer-photos/${p.photo_id}/image`;
              return (
                <div key={p.photo_id} className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setZoomSrc(src); setZoomMeta({ ...p, _columnTitle: title }); }}
                    className="block w-full cursor-zoom-in hover:opacity-90 transition"
                    title="Click para ampliar"
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full object-contain max-h-[40vh] bg-stone-50"
                      loading="lazy"
                    />
                  </button>
                  <div className="px-2 py-1 text-[10px] text-stone-500 bg-stone-50 border-t border-stone-100 flex items-center justify-between">
                    <span><b>{p.device}</b></span>
                    <span>{(p.taken_at || '').slice(0, 10)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={`${testIdPrefix}-trigger-btn`}
        onClick={() => setOpen(true)}
        className="h-7 text-xs gap-1 rounded-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50"
      >
        <ImageIcon size={12} weight="fill" />
        {triggerLabel || 'Ver foto del voluntario'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon size={18} className="text-indigo-600" />
              Comparativa visual — Voluntario {volunteerCode}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Baseline ({BASELINE_VISIT_CODE}) vs. visita actual ({currentVisitCode || '—'}){scaleMeta ? ` vs. ${scaleMeta.name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden flex gap-3 pr-1">
            <Column title="Foto basal" subtitle={BASELINE_VISIT_CODE} items={baselinePhotos} accent="emerald" />
            <Column title="Visita actual" subtitle={currentVisitCode} items={currentPhotos} accent="indigo" />
            {(scaleMeta?.file_id || referenceImageUrl) && (
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider mb-1.5 px-1">
                  Escala referencia
                  {scaleMeta && <span className="ml-2 text-stone-400 normal-case font-normal">({scaleMeta.name})</span>}
                </div>
                <div className="flex-1 bg-stone-50 border border-stone-200 rounded-sm flex items-center justify-center p-2 min-h-[200px]">
                  {(() => {
                    const refSrc = referenceImageUrl || `${API_URL}/api/public/derm-scales/${scaleId}/image`;
                    return (
                      <button
                        type="button"
                        onClick={() => { setZoomSrc(refSrc); setZoomMeta({ _columnTitle: 'Escala referencia', _isRef: true, name: scaleMeta?.name }); }}
                        className="cursor-zoom-in hover:opacity-90 transition"
                        title="Click para ampliar"
                      >
                        <img src={refSrc} alt="referencia" className="max-w-full max-h-[60vh] object-contain" />
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
          {loading && <div className="text-xs text-stone-400 text-center py-2">Cargando fotos…</div>}
          {!loading && photos.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-800">
              Este voluntario no tiene fotos subidas en este estudio. Sube fotos desde la pestaña Sondas → Fotografias - [dispositivo].
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Zoom dialog — open on top of the comparison dialog */}
      <Dialog open={!!zoomSrc} onOpenChange={v => { if (!v) { setZoomSrc(null); setZoomMeta(null); } }}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] p-2 flex flex-col bg-black/95 border-0">
          <DialogHeader className="px-2 py-1">
            <DialogTitle className="text-white text-sm flex items-center gap-2">
              <ImageIcon size={16} className="text-indigo-300" />
              {zoomMeta?._columnTitle || 'Foto'}
              {zoomMeta?.visit_code && <span className="text-indigo-300">· {zoomMeta.visit_code}</span>}
              {zoomMeta?.device && <span className="text-stone-400 text-xs font-normal">· {zoomMeta.device}</span>}
              {zoomMeta?.name && <span className="text-stone-400 text-xs font-normal">· {zoomMeta.name}</span>}
            </DialogTitle>
          </DialogHeader>
          {zoomSrc && <ZoomableImage src={zoomSrc} alt="zoom" />}
          {zoomMeta && !zoomMeta._isRef && (
            <div className="px-3 py-1.5 text-[11px] text-stone-300 flex items-center justify-between bg-black/40">
              <span>{zoomMeta.original_filename}</span>
              <span>{zoomMeta.taken_by} · {(zoomMeta.taken_at || '').slice(0, 16).replace('T', ' ')}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Interactive zoom + pan image viewer.
 * - Wheel: zoom in/out at cursor position
 * - Drag (when zoomed > 1): pan
 * - Buttons: +, -, fit, 1:1
 * - Double-click: cycle 1x -> 2x -> 4x -> 1x
 */
function ZoomableImage({ src, alt }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startPx: 0, startPy: 0 });
  const containerRef = useRef(null);

  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  const zoomAt = (clientX, clientY, factor) => {
    const el = containerRef.current;
    if (!el) { setScale(s => Math.max(0.5, Math.min(8, s * factor))); return; }
    const rect = el.getBoundingClientRect();
    const cx = clientX - rect.left - rect.width / 2;
    const cy = clientY - rect.top - rect.height / 2;
    setScale(prevScale => {
      const newScale = Math.max(0.5, Math.min(8, prevScale * factor));
      const ratio = newScale / prevScale;
      setPos(prevPos => ({
        x: cx - (cx - prevPos.x) * ratio,
        y: cy - (cy - prevPos.y) * ratio,
      }));
      return newScale;
    });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 0.87;
    zoomAt(e.clientX, e.clientY, factor);
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = {
      dragging: true, startX: e.clientX, startY: e.clientY,
      startPx: pos.x, startPy: pos.y,
    };
    e.preventDefault();
  };
  const handleMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPos({
      x: dragRef.current.startPx + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startPy + (e.clientY - dragRef.current.startY),
    });
  };
  const handleMouseUp = () => { dragRef.current.dragging = false; };

  const handleDoubleClick = (e) => {
    const cycle = scale < 1.5 ? 2 : scale < 3 ? 4 : 1;
    if (cycle === 1) { reset(); return; }
    zoomAt(e.clientX, e.clientY, cycle / scale);
  };

  return (
    <>
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-hidden flex items-center justify-center relative"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: scale > 1 ? (dragRef.current.dragging ? 'grabbing' : 'grab') : 'zoom-in', userSelect: 'none' }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: dragRef.current.dragging ? 'none' : 'transform 0.08s ease-out',
            maxWidth: '90vw',
            maxHeight: '80vh',
          }}
          className="select-none pointer-events-none"
        />
        {/* Floating controls */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 backdrop-blur rounded-sm p-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); const rect = containerRef.current.getBoundingClientRect(); zoomAt(rect.left + rect.width/2, rect.top + rect.height/2, 0.7); }}
            className="text-white hover:text-indigo-300 px-2 py-0.5 text-base font-bold"
            title="Reducir (rueda hacia abajo)"
          >−</button>
          <span className="text-white text-xs font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); const rect = containerRef.current.getBoundingClientRect(); zoomAt(rect.left + rect.width/2, rect.top + rect.height/2, 1.4); }}
            className="text-white hover:text-indigo-300 px-2 py-0.5 text-base font-bold"
            title="Ampliar (rueda hacia arriba)"
          >+</button>
          <div className="w-px h-4 bg-stone-600 mx-1" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); reset(); }}
            className="text-white hover:text-indigo-300 px-2 py-0.5 text-[11px] font-medium"
            title="Restablecer zoom"
          >RESET</button>
        </div>
        {/* Hint */}
        {scale === 1 && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-2.5 py-1 rounded-full pointer-events-none">
            Rueda del ratón para zoom · Doble click para 2×/4× · Arrastra para mover
          </div>
        )}
      </div>
    </>
  );
}
